import { APIError } from "web-core/utils";
import { useAuthContext } from "@/providers/auth-provider";
import { handleError } from "@/utils/error";
import { ConditionalRenderer } from "@core/components/conditional-renderer";
import { useGenerateDistributorReport } from "creso-core/hooks";
import { format, parse } from "date-fns";
import { InfoIcon, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  V2Label,
  VisuallyHidden,
} from "web-design-system";

function getErrorTitle(code: string) {
  const brokerageReportErrorCodeToTitle: Record<string, string> = {
    REPORT_GENERATION_ERROR: "Brokerage statement not available",
  };
  return (
    brokerageReportErrorCodeToTitle[code] ||
    "Failed to download brokerage report"
  );
}

export function BrokerageStatementDialog() {
  const { user } = useAuthContext();

  const monthOptions = useMemo(() => {
    const today = new Date();
    const joinDate = new Date(user.date_joined);

    const options = [];
    let current = new Date(joinDate.getFullYear(), joinDate.getMonth(), 1);
    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);

    while (current <= lastMonth) {
      options.push({
        label: format(current, "MMM yyyy"),
        value: format(current, "dd-MM-yyyy"),
      });
      current = new Date(current.getFullYear(), current.getMonth() + 1, 1);
    }

    options.reverse();

    return options;
  }, [user.date_joined]);

  const [selectedMonth, setSelectedMonth] = useState(monthOptions[0]?.value);

  const { mutateAsync: generateReport } = useGenerateDistributorReport();

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      const selectedDateObj = parse(selectedMonth, "dd-MM-yyyy", new Date());
      const startDate = format(
        new Date(selectedDateObj.getFullYear(), selectedDateObj.getMonth(), 1),
        "yyyy-MM-dd"
      );
      const endDate = format(
        new Date(
          selectedDateObj.getFullYear(),
          selectedDateObj.getMonth() + 1,
          0
        ),
        "yyyy-MM-dd"
      );
      const result = await generateReport({
        file_type: "excel",
        start_date: startDate,
        end_date: endDate,
        arn_code: user.username,
        type: "brokerage_report",
      });

      const reportUrl = result.report_url;
      const fileExtension = reportUrl?.split(".").pop();
      const response = await fetch(reportUrl);
      if (!response.ok) {
        throw new Error("Failed to generate brokerage report");
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${user.username}_brokerage_report_${format(startDate, "yyyy-MM-dd")}_to_${format(endDate, "yyyy-MM-dd")}.${fileExtension}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Brokerage report generated successfully");
    } catch (e) {
      if (e instanceof APIError) {
        handleError(e, getErrorTitle(e.data.code));
      } else {
        toast.error("Failed to download brokerage report");
      }
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button>Brokerage statement</Button>
      </DialogTrigger>
      <DialogContent className="gap-0 w-100">
        <VisuallyHidden>
          <DialogHeader>
            <DialogTitle>Brokerage statement</DialogTitle>
            <DialogDescription>Download brokerage Statement</DialogDescription>
          </DialogHeader>
        </VisuallyHidden>
        <span className="text-lg font-semibold">Brokerage statement</span>
        <span className="text-sm mt-2 text-muted-foreground">
          Commissions paid by the 20th of each month.
        </span>
        <ConditionalRenderer condition={monthOptions.length === 0}>
          <div className="flex flex-row items-center gap-3 mt-5 p-3 rounded-lg bg-card border border-border">
            <InfoIcon />
            <p className="text-sm text-muted-foreground">
              Once AUM is added, you can download the brokerage statement.
            </p>
          </div>
        </ConditionalRenderer>
        <ConditionalRenderer condition={monthOptions.length > 0}>
          <div className="h-px bg-border/50 my-5" />
          <V2Label className="mb-1 font-normal">Choose Month</V2Label>
          <Select
            value={selectedMonth}
            onValueChange={(v) => setSelectedMonth(v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-80">
              {monthOptions.map((m) => (
                <SelectItem key={m.value} value={m.value}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter className="mt-5">
            <Button
              disabled={isDownloading}
              onClick={handleDownload}
              className="w-full"
            >
              {isDownloading ? <Loader2 className="animate-spin" /> : null}
              {isDownloading ? "Downloading..." : "Download"}
            </Button>
          </DialogFooter>
        </ConditionalRenderer>
      </DialogContent>
    </Dialog>
  );
}
